// src/services/ChatAiApi.js
import {
  getApiBaseUrl,
  getApiHeaders,
  API_TIMEOUTS,
  fetchWithTimeout,
  handleApiError,
  requestQueueManager,
} from "./apiConfig";
import webSocketService from "./WebSocketService";

const API_BASE_URL = getApiBaseUrl();

export const chatAiApi = {
  // Chat Operations with WebSocket streaming for agent mode
  sendMessage: async (messageData, onProgress = null) => {
    console.log("🚀 Sending chat request...");

    // Check if this is an agent mode request
    if (messageData.mode === 'bedrock-agent') {
      console.log("📡 Using WebSocket streaming for agent mode");
      return await chatAiApi.sendMessageViaWebSocket(messageData, onProgress);
    }

    // Fall back to HTTP for non-agent modes
    console.log("🌐 Using HTTP for non-agent mode");
    return await chatAiApi.sendMessageViaHttp(messageData, onProgress);
  },

  // WebSocket-based message sending for agent mode
  sendMessageViaWebSocket: async (messageData, onProgress = null) => {
    console.log("📡 Sending message via WebSocket streaming...");

    return new Promise(async (resolve, reject) => {
      let responseData = {
        response: '',
        citations: [],
        reasoningSteps: [],
        sessionId: messageData.sessionId,
        messageId: messageData.messageId, // Preserve original messageId for feedback
        userId: messageData.userId,
        conversationTurn: 1,
        mode: 'bedrock-agent',
        metadata: {}
      };

      let startTime = Date.now();
      let chunkCount = 0;
      let isComplete = false;

      // Set up message handlers
      const unsubscribers = [];

      // Handle connection events
      const connectionHandler = (event) => {
        if (event.type === 'connected') {
          console.log('✅ WebSocket connected for chat');
          if (onProgress) {
            onProgress('Connected to real-time chat service...');
          }
        } else if (event.type === 'disconnected') {
          console.log('🔌 WebSocket disconnected during chat');
          if (!isComplete) {
            reject(new Error('WebSocket connection lost during chat'));
          }
        }
      };

      // Handle processing started
      const processingHandler = (message) => {
        console.log('🔄 Processing started');
        if (onProgress) {
          onProgress('Processing your request...');
        }
      };

      // Handle agent started
      const agentStartedHandler = (message) => {
        console.log('🚀 Agent processing started');
        startTime = Date.now();
        if (onProgress) {
          onProgress('Bedrock Agent is processing your query...');
        }
      };

      // Handle agent invoked
      const agentInvokedHandler = (message) => {
        console.log('⚡ Agent invoked');
        if (onProgress) {
          onProgress('Agent invoked, waiting for response...');
        }
      };

      // Handle streaming chunks
      const chunkHandler = (message) => {
        const chunk = message.data.chunk;
        // Skip raw trace JSON blobs - agent internals, not response content
        if (chunk && chunk.trimStart().startsWith('{"trace":')) return;
        chunkCount++;
        responseData.response += chunk;
        
        console.log(`📦 Received chunk ${chunkCount}: +${message.data.chunk.length} chars`);
        
        if (onProgress) {
          const elapsedTime = Date.now() - startTime;
          // Pass both progress message and streaming content
          onProgress(
            `Receiving response... (${chunkCount} chunks, ${Math.floor(elapsedTime/1000)}s)`,
            responseData.response // Pass the accumulated response for real-time display
          );
        }
      };

      // Handle progress updates
      const progressHandler = (message) => {
        console.log(`📊 Progress: ${message.data.message}`);
        if (onProgress) {
          onProgress(message.data.message);
        }
      };

      // Handle reasoning steps
      const reasoningHandler = (message) => {
        console.log(`🧠 Reasoning step ${message.data.stepNumber}`);
        responseData.reasoningSteps.push(message.data.step);
        
        if (onProgress) {
          onProgress(`Processing reasoning step ${message.data.stepNumber}...`);
        }
      };

      // Handle trace information
      const traceHandler = (message) => {
        console.log(`🔍 Trace chunk ${message.data.chunkNumber}`);
        if (onProgress) {
          const elapsedTime = Date.now() - startTime;
          onProgress(`Agent reasoning... (${Math.floor(elapsedTime/1000)}s elapsed)`);
        }
      };

      // Handle completion
      const completeHandler = (message) => {
        isComplete = true;
        const duration = Date.now() - startTime;
        
        console.log(`🎉 Agent response complete in ${duration}ms`);
        
        // Update response data with final results
        responseData.response = message.data.response;
        responseData.citations = message.data.citations || [];
        responseData.reasoningSteps = message.data.reasoningSteps || [];
        responseData.sessionId = message.data.sessionId || responseData.sessionId;
        responseData.messageId = message.data.messageId || responseData.messageId; // Preserve messageId for feedback
        responseData.agentId = message.data.agentId;
        responseData.processingTime = message.data.processingTime;
        responseData.metadata = {
          ...message.data.metadata,
          chunkCount,
          processingTimeMs: duration,
          wasStreamed: true
        };

        // Send final progress update to stop streaming indicator
        if (onProgress) {
          onProgress('Response complete', responseData.response);
        }

        // Clean up handlers
        unsubscribers.forEach(unsub => unsub());

        console.log("✅ WebSocket chat response received successfully");
        resolve(responseData);
      };

      // Handle errors
      const errorHandler = (message) => {
        isComplete = true;
        console.log(`❌ Agent error: ${message.data.error}`);
        
        // Clean up handlers
        unsubscribers.forEach(unsub => unsub());
        
        reject(new Error(`Agent error: ${message.data.error}`));
      };

      // Handle WebSocket errors
      const wsErrorHandler = (error) => {
        if (!isComplete) {
          console.log(`❌ WebSocket error: ${error.message}`);
          
          // Clean up handlers
          unsubscribers.forEach(unsub => unsub());
          
          reject(new Error(`WebSocket error: ${error.message}`));
        }
      };

      try {
        // Set up all message handlers
        unsubscribers.push(webSocketService.onConnection(connectionHandler));
        unsubscribers.push(webSocketService.onMessage('processing_started', processingHandler));
        unsubscribers.push(webSocketService.onMessage('agent_started', agentStartedHandler));
        unsubscribers.push(webSocketService.onMessage('agent_invoked', agentInvokedHandler));
        unsubscribers.push(webSocketService.onMessage('agent_chunk', chunkHandler));
        unsubscribers.push(webSocketService.onMessage('agent_progress', progressHandler));
        unsubscribers.push(webSocketService.onMessage('agent_reasoning', reasoningHandler));
        unsubscribers.push(webSocketService.onMessage('agent_trace', traceHandler));
        unsubscribers.push(webSocketService.onMessage('agent_complete', completeHandler));
        unsubscribers.push(webSocketService.onMessage('agent_error', errorHandler));
        unsubscribers.push(webSocketService.onMessage('error', errorHandler));
        unsubscribers.push(webSocketService.onError(wsErrorHandler));

        // Connect to WebSocket if not already connected
        await webSocketService.connect();

        // Send the chat message
        const sessionId = webSocketService.sendChatMessage(
          messageData.message,
          messageData.mode,
          messageData.sessionId,
          messageData.messageId // Pass messageId for feedback support
        );

        // Update response data with session ID
        responseData.sessionId = sessionId;
        
        console.log("📡 WebSocket message sent with IDs:", {
          messageId: messageData.messageId,
          sessionId: sessionId,
          mode: messageData.mode
        });

        // Set up timeout for WebSocket response (much longer than HTTP)
        setTimeout(() => {
          if (!isComplete) {
            console.log('⏰ WebSocket chat timed out after 10 minutes');
            
            // Clean up handlers
            unsubscribers.forEach(unsub => unsub());
            
            reject(new Error('Chat request timed out after 10 minutes'));
          }
        }, 600000); // 10 minutes timeout

      } catch (error) {
        console.log(`❌ WebSocket setup error: ${error.message}`);
        
        // Clean up handlers
        unsubscribers.forEach(unsub => unsub());
        
        reject(error);
      }
    });
  },

  // HTTP-based message sending for non-agent modes (fallback)
  sendMessageViaHttp: async (messageData, onProgress = null) => {
    console.log("🌐 Sending message via HTTP...");

    // Detect if this might be a complex SQL query
    const isComplexQuery =
      messageData.message &&
      (messageData.message.toLowerCase().includes("sql") ||
        messageData.message.toLowerCase().includes("select") ||
        messageData.message.toLowerCase().includes("from") ||
        messageData.message.length > 500);

    const timeout = isComplexQuery
      ? API_TIMEOUTS.LONG_RUNNING
      : API_TIMEOUTS.CHAT;
    console.log(
      `📊 Using ${timeout / 1000}s timeout for ${
        isComplexQuery ? "complex" : "standard"
      } query`
    );

    if (onProgress) {
      onProgress(
        `Processing ${
          isComplexQuery ? "complex" : ""
        } query... This may take up to ${Math.ceil(timeout / 60000)} minutes.`
      );

      // Update progress every 30 seconds to show we're still waiting
      const progressInterval = setInterval(() => {
        const elapsedTime = Date.now() - startTime;
        const elapsedMinutes = Math.floor(elapsedTime / 60000);
        const elapsedSeconds = Math.floor((elapsedTime % 60000) / 1000);
        onProgress(
          `Still processing... Elapsed time: ${elapsedMinutes}m ${elapsedSeconds}s (max: ${Math.ceil(
            timeout / 60000
          )} minutes)`
        );
      }, 30000);

      // Clear interval when request completes (we'll do this in finally block)
      messageData._progressInterval = progressInterval;
    }

    const startTime = Date.now();
    console.log(
      `⏱️ Starting request at ${new Date().toISOString()}, timeout set to ${timeout}ms`
    );

    try {
      // Determine priority based on query complexity and user context
      const priority = isComplexQuery ? 'normal' : 'high'; // Simple queries get higher priority
      
      console.log(`📊 Queuing ${isComplexQuery ? 'complex' : 'simple'} chat request with ${priority} priority`);
      
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/chat`,
        {
          method: "POST",
          headers: getApiHeaders(),
          body: JSON.stringify(messageData),
        },
        timeout,
        'chat',
        priority
      );

      const elapsedTime = Date.now() - startTime;
      console.log(`⏱️ Request completed in ${elapsedTime}ms`);

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(
          `HTTP error! Status: ${response.status}, Message: ${errorData}`
        );
      }

      const result = await response.json();
      console.log("✅ Chat response received successfully");
      
      // Log token usage if available
      if (result.usage) {
        console.log("📊 Token usage:", result.usage);
      }
      
      return result;
    } catch (error) {
      const elapsedTime = Date.now() - startTime;
      console.log(
        `❌ Request failed after ${elapsedTime}ms with error: ${error.message}`
      );
      console.log(`🔍 Expected timeout was ${timeout}ms (${timeout / 1000}s)`);

      // Check if this is actually our timeout or something else
      if (error.name === "AbortError" || error.message.includes("timed out")) {
        console.log(`⏰ Confirmed timeout after ${elapsedTime}ms`);
      }
      // If we get a timeout, try to check if the response might be available via conversation API
      if (error.message.includes("timed out") && messageData.sessionId) {
        console.log(
          "⏰ Request timed out, checking if response was processed..."
        );
        if (onProgress) {
          onProgress(
            "Request timed out, but checking if response was processed in the background..."
          );
        }

        // Wait a moment then try to get the latest conversation
        await new Promise((resolve) => setTimeout(resolve, 2000));

        try {
          const conversation = await chatAiApi.getConversation(
            messageData.sessionId
          );
          const latestMessage =
            conversation.messages?.[conversation.messages.length - 1];

          // Check if there's a new response that matches our request
          if (
            latestMessage &&
            latestMessage.messageId !== messageData.messageId
          ) {
            console.log("🎯 Found processed response in conversation history");
            return {
              response: latestMessage.generatedResponse,
              citations: latestMessage.citations || [],
              messageId: latestMessage.messageId,
              sessionId: messageData.sessionId,
              userId: messageData.userId,
              conversationTurn: conversation.messages.length,
            };
          }
        } catch (convError) {
          console.log("❌ Could not retrieve conversation:", convError.message);
        }
      }

      throw error;
    } finally {
      // Clear the progress interval if it was set
      if (messageData._progressInterval) {
        clearInterval(messageData._progressInterval);
        delete messageData._progressInterval;
      }
    }
  },

  // Models Operations
  getAvailableModels: async () => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/models`,
      {
        method: "GET",
        headers: getApiHeaders(),
      },
      API_TIMEOUTS.DEFAULT,
      'api',
      'high' // High priority for UI initialization
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  },

  // Data Sources Operations
  getDataSources: async () => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/datasources`,
      {
        method: "GET",
        headers: getApiHeaders(),
      },
      API_TIMEOUTS.DEFAULT,
      'api',
      'high' // High priority for UI initialization
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  },

  // Conversations Operations
  getConversation: async (sessionId) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/conversations/${sessionId}`,
      {
        method: "GET",
        headers: getApiHeaders(),
      },
      API_TIMEOUTS.DEFAULT,
      'api',
      'high' // High priority for conversation restoration
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  },

  // Feedback Operations
  submitFeedback: async (feedbackData) => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/feedback`,
      {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify(feedbackData),
      },
      API_TIMEOUTS.DEFAULT,
      'api',
      'normal' // Normal priority for feedback
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to submit feedback");
    }

    return response.json();
  },

  // Health Check (using agent status as health indicator)
  checkApiStatus: async () => {
    // Use agent status endpoint as health check
    return await chatAiApi.getAgentStatus();
  },

  // Agent Operations
  getAgentStatus: async () => {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/agent/status`,
      {
        method: "GET",
        headers: getApiHeaders(),
      },
      API_TIMEOUTS.DEFAULT,
      'api',
      'high' // High priority for agent status
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  },

  // Queue Management Operations
  getQueueStatus: () => {
    return requestQueueManager.getQueueStatus();
  },

  clearQueue: () => {
    requestQueueManager.clearQueue();
  },
};
